import { createNoise2D, createNoise3D } from 'simplex-noise';
import alea from 'alea';
import { CHUNK_SIZE, TERRAIN_BASE, TERRAIN_AMPLITUDE, SEA_LEVEL } from '../constants.js';
import { BlockId } from './BlockType.js';
import { StructureGenerator } from './StructureGenerator.js';

const BIOME = { PLAINS: 0, DESERT: 1, FOREST: 2, TUNDRA: 3, MOUNTAINS: 4, JUNGLE: 5, SWAMP: 6, SAVANNA: 7 };

export class TerrainGenerator {
  constructor(seed) {
    this.noise2D = createNoise2D(alea(seed + '-2d'));
    this.noise3D = createNoise3D(alea(seed + '-3d'));
    this.caveNoise = createNoise3D(alea(seed + '-cave'));
    this.treeNoise = createNoise2D(alea(seed + '-tree'));
    this.treeDetail = createNoise2D(alea(seed + '-treedetail'));
    this.tempNoise = createNoise2D(alea(seed + '-temp'));
    this.humidNoise = createNoise2D(alea(seed + '-humid'));
    this.villageNoise = createNoise2D(alea(seed + '-village'));
    this.oreNoise = createNoise3D(alea(seed + '-ore'));
    this.oreNoise2 = createNoise3D(alea(seed + '-ore2'));
    this.oreNoise3 = createNoise3D(alea(seed + '-ore3'));
    this.flowerNoise = createNoise2D(alea(seed + '-flower'));

    // Cache for height lookups during structure gen
    this._heightCache = new Map();

    // Structure generator for dungeons, mines, temples, portals
    this.structureGenerator = new StructureGenerator(seed);

    // Fixed village locations (always accessible)
    this._villaMiseriaPos = null;
    this._villaProsperidadPos = null;
    this._ciudadPos = null;
  }

  getCiudadPosition() {
    if (this._ciudadPos) return this._ciudadPos;
    // Grid (-1, 1) — separate from villas, west-north of spawn
    const gridSize = 80;
    const gx = -1, gz = 1;
    const vx = gx * gridSize + 8;
    const vz = gz * gridSize + 8;
    const vy = this._getHeight(vx, vz) + 2;
    this._ciudadPos = { x: vx, y: vy, z: vz };
    return this._ciudadPos;
  }

  getVillaMiseriaPosition() {
    if (this._villaMiseriaPos) return this._villaMiseriaPos;
    const gridSize = 80;
    const gx = 1, gz = 0;
    const vx = gx * gridSize + Math.floor((this.villageNoise(gx * 0.7, gz * 0.7) + 1) * 15);
    const vz = gz * gridSize + Math.floor((this.villageNoise(gx * 0.3 + 100, gz * 0.3 + 100) + 1) * 15);
    const vy = this._getHeight(vx, vz) + 2;
    this._villaMiseriaPos = { x: vx, y: vy, z: vz };
    return this._villaMiseriaPos;
  }

  getVillaProsperidadPosition() {
    if (this._villaProsperidadPos) return this._villaProsperidadPos;
    const gridSize = 80;
    const gx = 0, gz = 1;
    const vx = gx * gridSize + Math.floor((this.villageNoise(gx * 0.7, gz * 0.7) + 1) * 15);
    const vz = gz * gridSize + Math.floor((this.villageNoise(gx * 0.3 + 100, gz * 0.3 + 100) + 1) * 15);
    const vy = this._getHeight(vx, vz) + 2;
    this._villaProsperidadPos = { x: vx, y: vy, z: vz };
    return this._villaProsperidadPos;
  }

  fillChunk(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const wx = wx0 + lx;
        const wz = wz0 + lz;
        const surfaceHeight = this._getHeight(wx, wz);
        const biome = this._getBiome(wx, wz);

        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
          const wy = wy0 + ly;
          const blockId = this._getBlockAt(wx, wy, wz, surfaceHeight, biome);
          if (blockId !== BlockId.AIR) {
            chunk.setBlock(lx, ly, lz, blockId);
          }
        }
      }
    }

    // Structure pass (trees, cacti, villages)
    this._generateStructures(chunk);
    chunk.isDirty = true;
  }

  getHeight(wx, wz) {
    return this._getHeight(wx, wz);
  }

  getBiome(wx, wz) {
    return this._getBiome(wx, wz);
  }

  _getHeight(wx, wz) {
    const key = (wx & 0xFFFF) << 16 | (wz & 0xFFFF);
    if (this._heightCache.has(key)) return this._heightCache.get(key);

    let height = 0;
    let amplitude = 1;
    let frequency = 0.01;
    let maxAmplitude = 0;

    for (let i = 0; i < 4; i++) {
      height += this.noise2D(wx * frequency, wz * frequency) * amplitude;
      maxAmplitude += amplitude;
      amplitude *= 0.5;
      frequency *= 2.0;
    }

    height = (height / maxAmplitude + 1) / 2;
    const result = Math.floor(TERRAIN_BASE + height * TERRAIN_AMPLITUDE);

    // Limit cache size
    if (this._heightCache.size > 100000) this._heightCache.clear();
    this._heightCache.set(key, result);
    return result;
  }

  _getBiome(wx, wz) {
    const temp = this.tempNoise(wx * 0.004, wz * 0.004);
    const humid = this.humidNoise(wx * 0.004, wz * 0.004);
    const height = this._getHeight(wx, wz);

    if (height > TERRAIN_BASE + TERRAIN_AMPLITUDE * 0.75) return BIOME.MOUNTAINS;
    if (temp < -0.3) return BIOME.TUNDRA;
    if (temp > 0.25 && humid < 0.0) return BIOME.DESERT;
    if (temp > 0.3 && humid > 0.3) return BIOME.JUNGLE;
    if (temp > 0.1 && humid > 0.0 && humid < 0.3) return BIOME.SAVANNA;
    if (temp > -0.1 && temp < 0.15 && humid > 0.2) return BIOME.SWAMP;
    if (humid > 0.1) return BIOME.FOREST;
    return BIOME.PLAINS;
  }

  _getBlockAt(wx, wy, wz, surfaceHeight, biome) {
    // Bedrock layer
    if (wy === 0) return BlockId.BEDROCK;
    if (wy <= 2) {
      const v = Math.abs(this.noise3D(wx * 0.5, wy * 0.5, wz * 0.5));
      if (v < 0.5) return BlockId.BEDROCK;
    }

    // Water fill below sea level
    if (wy > surfaceHeight && wy <= SEA_LEVEL) {
      return BlockId.WATER;
    }

    // Above surface = air
    if (wy > surfaceHeight) return BlockId.AIR;

    // Cave carving
    if (wy < surfaceHeight - 5 && wy > 3) {
      const caveVal = this.caveNoise(wx * 0.05, wy * 0.05, wz * 0.05);
      if (caveVal > 0.6) return BlockId.AIR;
    }

    // Surface block by biome
    if (wy === surfaceHeight) {
      if (surfaceHeight <= SEA_LEVEL) return BlockId.SAND;
      switch (biome) {
        case BIOME.DESERT: return BlockId.SAND;
        case BIOME.TUNDRA: return BlockId.SNOW_GRASS;
        case BIOME.MOUNTAINS:
          return surfaceHeight > TERRAIN_BASE + TERRAIN_AMPLITUDE * 0.85
            ? BlockId.SNOW_GRASS : BlockId.STONE;
        default: return BlockId.GRASS;
      }
    }

    // Subsurface
    if (wy > surfaceHeight - 4) {
      switch (biome) {
        case BIOME.DESERT:
          return wy > surfaceHeight - 3 ? BlockId.SAND : BlockId.SANDSTONE;
        default: return BlockId.DIRT;
      }
    }

    // Ore generation in stone
    return this._getOre(wx, wy, wz);
  }

  _getOre(wx, wy, wz) {
    // Each ore uses different noise frequencies and thresholds
    // Coal: y 5-80, common
    if (wy >= 5 && wy <= 80) {
      const v = this.oreNoise(wx * 0.1, wy * 0.1, wz * 0.1);
      if (v > 0.75) return BlockId.COAL_ORE;
    }
    // Iron: y 5-64, medium
    if (wy >= 5 && wy <= 64) {
      const v = this.oreNoise2(wx * 0.12, wy * 0.12, wz * 0.12);
      if (v > 0.8) return BlockId.IRON_ORE;
    }
    // Gold: y 5-32, rare
    if (wy >= 5 && wy <= 32) {
      const v = this.oreNoise3(wx * 0.15, wy * 0.15, wz * 0.15);
      if (v > 0.87) return BlockId.GOLD_ORE;
    }
    // Diamond: y 5-16, very rare
    if (wy >= 5 && wy <= 16) {
      const v = this.oreNoise(wx * 0.2 + 100, wy * 0.2, wz * 0.2 + 100);
      if (v > 0.9) return BlockId.DIAMOND_ORE;
    }
    // Redstone: y 5-16, medium-rare
    if (wy >= 5 && wy <= 16) {
      const v = this.oreNoise2(wx * 0.15 + 200, wy * 0.15, wz * 0.15 + 200);
      if (v > 0.85) return BlockId.REDSTONE_ORE;
    }
    // Lapis: y 10-30, rare
    if (wy >= 10 && wy <= 30) {
      const v = this.oreNoise3(wx * 0.18 + 300, wy * 0.18, wz * 0.18 + 300);
      if (v > 0.9) return BlockId.LAPIS_ORE;
    }
    // Emerald: y 5-32, very rare, mountains only
    if (wy >= 5 && wy <= 32) {
      const v = this.oreNoise(wx * 0.25 + 500, wy * 0.25, wz * 0.25 + 500);
      if (v > 0.95) return BlockId.EMERALD_ORE;
    }
    // Gravel patches underground
    if (wy >= 5 && wy <= 60) {
      const v = this.oreNoise2(wx * 0.08 + 400, wy * 0.08, wz * 0.08 + 400);
      if (v > 0.85) return BlockId.GRAVEL;
    }
    return BlockId.STONE;
  }

  _generateStructures(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Trees and cacti: check wider area for structures that may extend into this chunk
    const pad = 5; // Wider padding for jungle trees (radius 4 canopy)
    for (let wz = wz0 - pad; wz < wz0 + CHUNK_SIZE + pad; wz++) {
      for (let wx = wx0 - pad; wx < wx0 + CHUNK_SIZE + pad; wx++) {
        const surfaceHeight = this._getHeight(wx, wz);
        const biome = this._getBiome(wx, wz);

        if (surfaceHeight <= SEA_LEVEL + 1) continue;

        if (this._shouldPlaceTree(wx, wz, biome)) {
          this._placeTree(chunk, wx, surfaceHeight, wz, biome);
        } else if (biome === BIOME.DESERT && this._shouldPlaceCactus(wx, wz)) {
          this._placeCactus(chunk, wx, surfaceHeight, wz);
        } else if (surfaceHeight > SEA_LEVEL + 1 && biome !== BIOME.DESERT) {
          // Flowers, tall grass, mushrooms
          this._placeVegetation(chunk, wx, surfaceHeight, wz, biome);
        }
      }
    }

    // Villages: check if any village center is near this chunk
    this._generateVillageStructures(chunk);

    // Underground + overworld structures (dungeons, mines, desert temples, ruined portals)
    this.structureGenerator.generate(
      chunk,
      (wx, wz) => this._getHeight(wx, wz),
      (wx, wz) => this._getBiome(wx, wz),
      BIOME.DESERT,
      BIOME.JUNGLE,
      BIOME.TUNDRA,
      BIOME.SWAMP
    );
  }

  _placeVegetation(chunk, wx, surfaceHeight, wz, biome) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const v = this.flowerNoise(wx * 2.3, wz * 2.3);
    const v2 = this.flowerNoise(wx * 5.1 + 100, wz * 5.1 + 100);

    // Tall grass (common in plains, forest, jungle, savanna)
    if (v > 0.3 && v < 0.7 && v2 > 0.5 && (biome === BIOME.PLAINS || biome === BIOME.FOREST || biome === BIOME.JUNGLE || biome === BIOME.SAVANNA)) {
      this._setInChunk(chunk, wx - wx0, surfaceHeight + 1 - wy0, wz - wz0, BlockId.TALL_GRASS);
      return;
    }
    // Red flowers (uncommon, not in swamp or savanna)
    if (v > 0.85 && biome !== BIOME.TUNDRA && biome !== BIOME.SWAMP && biome !== BIOME.SAVANNA) {
      this._setInChunk(chunk, wx - wx0, surfaceHeight + 1 - wy0, wz - wz0, BlockId.FLOWER_RED);
      return;
    }
    // Yellow flowers (uncommon, not in swamp or savanna)
    if (v < -0.85 && biome !== BIOME.TUNDRA && biome !== BIOME.SWAMP && biome !== BIOME.SAVANNA) {
      this._setInChunk(chunk, wx - wx0, surfaceHeight + 1 - wy0, wz - wz0, BlockId.FLOWER_YELLOW);
      return;
    }
    // Mushrooms (rare, in forest, jungle, swamp)
    if ((biome === BIOME.FOREST || biome === BIOME.JUNGLE || biome === BIOME.SWAMP) && v2 > 0.9) {
      const mushroomType = v > 0 ? BlockId.MUSHROOM_RED : BlockId.MUSHROOM_BROWN;
      this._setInChunk(chunk, wx - wx0, surfaceHeight + 1 - wy0, wz - wz0, mushroomType);
      return;
    }
    // Pumpkins (very rare, plains only)
    if (biome === BIOME.PLAINS && v > 0.92 && v2 > 0.8) {
      this._setInChunk(chunk, wx - wx0, surfaceHeight + 1 - wy0, wz - wz0, BlockId.PUMPKIN);
    }
  }

  _shouldPlaceTree(wx, wz, biome) {
    if (biome === BIOME.DESERT) return false;

    const val = Math.abs(this.treeNoise(wx * 0.8, wz * 0.8));
    const detail = Math.abs(this.treeDetail(wx * 5.1, wz * 5.1));

    let threshold;
    switch (biome) {
      case BIOME.FOREST: threshold = 0.55; break;
      case BIOME.JUNGLE: threshold = 0.45; break;
      case BIOME.SWAMP: threshold = 0.80; break;
      case BIOME.SAVANNA: threshold = 0.90; break;
      case BIOME.TUNDRA: threshold = 0.88; break;
      case BIOME.MOUNTAINS: threshold = 0.92; break;
      default: threshold = 0.82; break;
    }

    return val > threshold && detail > 0.4;
  }

  _placeTree(chunk, tx, surfaceHeight, tz, biome) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    if (biome === BIOME.JUNGLE) {
      this._placeJungleTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0);
    } else if (biome === BIOME.SWAMP) {
      this._placeSwampTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0);
    } else if (biome === BIOME.SAVANNA) {
      this._placeAcaciaTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0);
    } else {
      // Default oak tree (existing behavior)
      this._placeOakTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0);
    }
  }

  _placeOakTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0) {
    const trunkH = 4 + Math.floor(Math.abs(this.treeDetail(tx * 2.3, tz * 2.3)) * 3);

    // Trunk
    for (let h = 1; h <= trunkH; h++) {
      this._setInChunk(chunk, tx - wx0, surfaceHeight + h - wy0, tz - wz0, BlockId.WOOD);
    }

    // Canopy
    const canopyBase = surfaceHeight + trunkH - 1;
    const canopyTop = surfaceHeight + trunkH + 2;

    for (let cy = canopyBase; cy <= canopyTop; cy++) {
      const radius = cy < canopyTop ? 2 : 1;
      for (let cx = -radius; cx <= radius; cx++) {
        for (let cz = -radius; cz <= radius; cz++) {
          if (Math.abs(cx) === radius && Math.abs(cz) === radius && cy === canopyBase) continue;
          if (cx === 0 && cz === 0 && cy < surfaceHeight + trunkH + 1) continue;

          this._setInChunk(chunk, tx + cx - wx0, cy - wy0, tz + cz - wz0, BlockId.LEAVES);
        }
      }
    }
  }

  _placeJungleTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0) {
    const trunkH = 8 + Math.floor(Math.abs(this.treeDetail(tx * 2.3, tz * 2.3)) * 6);

    // Trunk
    for (let h = 1; h <= trunkH; h++) {
      this._setInChunk(chunk, tx - wx0, surfaceHeight + h - wy0, tz - wz0, BlockId.JUNGLE_LOG);
    }

    // Wide canopy (radius 3-4)
    const canopyBase = surfaceHeight + trunkH - 2;
    const canopyTop = surfaceHeight + trunkH + 2;

    for (let cy = canopyBase; cy <= canopyTop; cy++) {
      const distFromTop = canopyTop - cy;
      const radius = distFromTop >= 2 ? 3 : (distFromTop >= 1 ? 4 : 2);
      for (let cx = -radius; cx <= radius; cx++) {
        for (let cz = -radius; cz <= radius; cz++) {
          // Round the canopy (skip far corners)
          if (Math.abs(cx) + Math.abs(cz) > radius + 1) continue;
          if (cx === 0 && cz === 0 && cy < surfaceHeight + trunkH + 1) continue;

          this._setInChunk(chunk, tx + cx - wx0, cy - wy0, tz + cz - wz0, BlockId.JUNGLE_LEAVES);
        }
      }
    }

    // Vines hanging from canopy edges (occasionally)
    const vineRng = Math.abs(this.treeDetail(tx * 7.7, tz * 7.7));
    if (vineRng > 0.3) {
      const vinePositions = [
        [-3, 0], [3, 0], [0, -3], [0, 3],
        [-2, -2], [2, 2], [-2, 2], [2, -2]
      ];
      for (const [vdx, vdz] of vinePositions) {
        const vr = Math.abs(this.treeDetail((tx + vdx) * 3.3, (tz + vdz) * 3.3));
        if (vr > 0.5) {
          const vineLen = 2 + Math.floor(vr * 3);
          for (let vi = 1; vi <= vineLen; vi++) {
            this._setInChunk(chunk, tx + vdx - wx0, canopyBase - vi - wy0, tz + vdz - wz0, BlockId.VINE);
          }
        }
      }
    }
  }

  _placeSwampTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0) {
    const trunkH = 4 + Math.floor(Math.abs(this.treeDetail(tx * 2.3, tz * 2.3)) * 2);

    // Trunk (normal oak wood)
    for (let h = 1; h <= trunkH; h++) {
      this._setInChunk(chunk, tx - wx0, surfaceHeight + h - wy0, tz - wz0, BlockId.WOOD);
    }

    // Canopy (normal oak leaves)
    const canopyBase = surfaceHeight + trunkH - 1;
    const canopyTop = surfaceHeight + trunkH + 2;

    for (let cy = canopyBase; cy <= canopyTop; cy++) {
      const radius = cy < canopyTop ? 2 : 1;
      for (let cx = -radius; cx <= radius; cx++) {
        for (let cz = -radius; cz <= radius; cz++) {
          if (Math.abs(cx) === radius && Math.abs(cz) === radius && cy === canopyBase) continue;
          if (cx === 0 && cz === 0 && cy < surfaceHeight + trunkH + 1) continue;

          this._setInChunk(chunk, tx + cx - wx0, cy - wy0, tz + cz - wz0, BlockId.LEAVES);
        }
      }
    }

    // Vines hanging from leaves (2-4 blocks down)
    const vinePositions = [[-2, 0], [2, 0], [0, -2], [0, 2], [-1, -1], [1, 1]];
    for (const [vdx, vdz] of vinePositions) {
      const vr = Math.abs(this.treeDetail((tx + vdx) * 4.1, (tz + vdz) * 4.1));
      if (vr > 0.4) {
        const vineLen = 2 + Math.floor(vr * 2);
        for (let vi = 1; vi <= vineLen; vi++) {
          this._setInChunk(chunk, tx + vdx - wx0, canopyBase - vi - wy0, tz + vdz - wz0, BlockId.VINE);
        }
      }
    }

    // Lily pads on nearby water
    for (let ldx = -3; ldx <= 3; ldx++) {
      for (let ldz = -3; ldz <= 3; ldz++) {
        const lwx = tx + ldx;
        const lwz = tz + ldz;
        const lh = this._getHeight(lwx, lwz);
        if (lh < SEA_LEVEL) {
          const lr = Math.abs(this.flowerNoise(lwx * 3.7, lwz * 3.7));
          if (lr > 0.7) {
            this._setInChunk(chunk, lwx - wx0, SEA_LEVEL + 1 - wy0, lwz - wz0, BlockId.LILY_PAD);
          }
        }
      }
    }
  }

  _placeAcaciaTree(chunk, tx, surfaceHeight, tz, wx0, wy0, wz0) {
    const trunkH = 4 + Math.floor(Math.abs(this.treeDetail(tx * 2.3, tz * 2.3)) * 2);

    // Trunk with slight angle (offset at top)
    const angleRng = this.treeDetail(tx * 1.7, tz * 1.7);
    const offsetX = angleRng > 0.3 ? 1 : (angleRng < -0.3 ? -1 : 0);
    const offsetZ = Math.abs(angleRng) < 0.3 ? (angleRng > 0 ? 1 : -1) : 0;

    for (let h = 1; h <= trunkH; h++) {
      const dx = h >= trunkH - 1 ? offsetX : 0;
      const dz = h >= trunkH - 1 ? offsetZ : 0;
      this._setInChunk(chunk, tx + dx - wx0, surfaceHeight + h - wy0, tz + dz - wz0, BlockId.ACACIA_LOG);
    }

    // Flat top canopy (wide but only 1-2 layers thick)
    const topX = tx + offsetX;
    const topZ = tz + offsetZ;
    const canopyY1 = surfaceHeight + trunkH + 1;
    const canopyY2 = surfaceHeight + trunkH + 2;

    for (let cy = canopyY1; cy <= canopyY2; cy++) {
      const radius = cy === canopyY1 ? 3 : 2;
      for (let cx = -radius; cx <= radius; cx++) {
        for (let cz = -radius; cz <= radius; cz++) {
          // Skip corners for more rounded shape
          if (Math.abs(cx) === radius && Math.abs(cz) === radius) continue;
          this._setInChunk(chunk, topX + cx - wx0, cy - wy0, topZ + cz - wz0, BlockId.ACACIA_LEAVES);
        }
      }
    }
  }

  _shouldPlaceCactus(wx, wz) {
    const val = Math.abs(this.treeNoise(wx * 1.5, wz * 1.5));
    const detail = Math.abs(this.treeDetail(wx * 7.3, wz * 7.3));
    return val > 0.78 && detail > 0.6;
  }

  _placeCactus(chunk, cx, surfaceHeight, cz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    const height = 1 + Math.floor(Math.abs(this.treeDetail(cx * 3.1, cz * 3.1)) * 3);

    for (let h = 1; h <= height; h++) {
      this._setInChunk(chunk, cx - wx0, surfaceHeight + h - wy0, cz - wz0, BlockId.CACTUS);
    }
  }

  // --- Village Generation ---
  _generateVillageStructures(chunk) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Check village grid positions near this chunk
    const gridSize = 80;
    const searchDist = 25; // max village radius

    const minGx = Math.floor((wx0 - searchDist) / gridSize);
    const maxGx = Math.floor((wx0 + CHUNK_SIZE + searchDist) / gridSize);
    const minGz = Math.floor((wz0 - searchDist) / gridSize);
    const maxGz = Math.floor((wz0 + CHUNK_SIZE + searchDist) / gridSize);

    for (let gx = minGx; gx <= maxGx; gx++) {
      for (let gz = minGz; gz <= maxGz; gz++) {
        const vx = gx * gridSize + Math.floor((this.villageNoise(gx * 0.7, gz * 0.7) + 1) * 15);
        const vz = gz * gridSize + Math.floor((this.villageNoise(gx * 0.3 + 100, gz * 0.3 + 100) + 1) * 15);
        const biome = this._getBiome(vx, vz);

        // Only spawn villages in plains or forest, on flat terrain
        if (biome !== BIOME.PLAINS && biome !== BIOME.FOREST) continue;

        const vNoise = this.villageNoise(gx * 1.3 + 50, gz * 1.3 + 50);
        if (vNoise < 0.4) continue;

        const surfaceH = this._getHeight(vx, vz);
        if (surfaceH <= SEA_LEVEL + 2) continue;

        // Grid (1,0) is always Villa Miseria, (0,1) is always Villa Prosperidad
        const isGuaranteedMiseria = (gx === 1 && gz === 0);
        const isGuaranteedProsperidad = (gx === 0 && gz === 1);
        const typeNoise = this.villageNoise(gx * 2.7 + 200, gz * 2.7 + 200);

        if (isGuaranteedMiseria || typeNoise <= 0.2) {
          this._placeVillaMiseria(chunk, vx, surfaceH, vz);
        } else if (isGuaranteedProsperidad || typeNoise > 0.85) {
          this._placeVillaProsperidad(chunk, vx, surfaceH, vz);
        } else {
          this._placeVillage(chunk, vx, surfaceH, vz);
        }
      }
    }

    // Ciudad: at grid (-1, 1), regardless of biome (it's a city, paves over land)
    const ciudadGx = -1, ciudadGz = 1;
    const cwx = ciudadGx * gridSize + 8;
    const cwz = ciudadGz * gridSize + 8;
    if (cwx + 40 >= wx0 - searchDist && cwx - 40 <= wx0 + CHUNK_SIZE + searchDist &&
        cwz + 40 >= wz0 - searchDist && cwz - 40 <= wz0 + CHUNK_SIZE + searchDist) {
      const csh = this._getHeight(cwx, cwz);
      if (csh > SEA_LEVEL + 1) {
        this._placeCiudad(chunk, cwx, csh, cwz);
      }
    }
  }

  // --- Ciudad moderna: tall buildings, asphalt streets, glass facades, lamp posts ---
  _placeCiudad(chunk, ccx, surfaceH, ccz) {
    // Layout: 4x4 grid of buildings + streets between them, total 36x36 block area
    // Each block-cell: 8x8 building footprint with 2-block-wide street
    const buildings = [
      { dx: -18, dz: -18, floors: 7,  style: 'office' },
      { dx:  -8, dz: -18, floors: 5,  style: 'office' },
      { dx:   2, dz: -18, floors: 9,  style: 'glass'  },
      { dx:  12, dz: -18, floors: 4,  style: 'house'  },
      { dx: -18, dz:  -8, floors: 6,  style: 'glass'  },
      { dx:  -8, dz:  -8, floors: 12, style: 'office' }, // central skyscraper
      { dx:   2, dz:  -8, floors: 5,  style: 'house'  },
      { dx:  12, dz:  -8, floors: 8,  style: 'glass'  },
      { dx: -18, dz:   2, floors: 4,  style: 'house'  },
      { dx:  -8, dz:   2, floors: 6,  style: 'office' },
      { dx:   2, dz:   2, floors: 10, style: 'glass'  },
      { dx:  12, dz:   2, floors: 3,  style: 'house'  },
      { dx: -18, dz:  12, floors: 5,  style: 'office' },
      { dx:  -8, dz:  12, floors: 4,  style: 'house'  },
      { dx:   2, dz:  12, floors: 7,  style: 'glass'  },
      { dx:  12, dz:  12, floors: 6,  style: 'office' },
    ];

    // Flatten + lay asphalt streets across the entire city footprint
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    for (let dx = -22; dx <= 22; dx++) {
      for (let dz = -22; dz <= 22; dz++) {
        const wx = ccx + dx;
        const wz = ccz + dz;
        const lx = wx - wx0;
        const lz = wz - wz0;
        if (lx < 0 || lx >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;
        const ly = surfaceH - wy0;
        if (ly < 0 || ly >= CHUNK_SIZE) continue;
        // Asphalt: dark cobblestone (placeholder until ASPHALT block exists)
        this._setInChunkForce(chunk, lx, ly, lz, BlockId.COBBLESTONE);
      }
    }

    // Place buildings
    for (const b of buildings) {
      const bx = ccx + b.dx;
      const bz = ccz + b.dz;
      const bh = this._getHeight(bx, bz);
      this._placeBuilding(chunk, bx, bh, bz, b.floors, b.style);
    }

    // Lamp posts at street corners
    const lampOffsets = [
      [-22, -22], [-22, 22], [22, -22], [22, 22],
      [-13, -13], [-13, 13], [13, -13], [13, 13],
      [0, -22], [0, 22], [-22, 0], [22, 0],
    ];
    for (const [lx, lz] of lampOffsets) {
      this._placeLampPost(chunk, ccx + lx, surfaceH, ccz + lz);
    }
  }

  _placeBuilding(chunk, bx, bh, bz, floors, style) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    // 8x8 footprint, 3 blocks per floor
    const W = 8;
    const totalH = floors * 3;

    let wallBlock = BlockId.COBBLESTONE;
    let trimBlock = BlockId.PLANKS;
    if (style === 'glass') { wallBlock = BlockId.GLASS; trimBlock = BlockId.COBBLESTONE; }
    else if (style === 'house') { wallBlock = BlockId.PLANKS; trimBlock = BlockId.COBBLESTONE; }

    for (let dy = 0; dy < totalH; dy++) {
      const floorIdx = Math.floor(dy / 3);
      const isWindowRow = (dy % 3) === 1; // middle row of each floor
      for (let dx = 0; dx < W; dx++) {
        for (let dz = 0; dz < W; dz++) {
          const wx = bx + dx;
          const wy = bh + 1 + dy;
          const wz = bz + dz;
          const lx = wx - wx0, ly = wy - wy0, lz = wz - wz0;
          if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

          const isEdge = (dx === 0 || dx === W - 1 || dz === 0 || dz === W - 1);
          const isCorner = ((dx === 0 || dx === W - 1) && (dz === 0 || dz === W - 1));
          const isFloorSlab = (dy % 3) === 0;
          const isTopFloor = (dy === totalH - 1);

          let block = 0;
          if (isFloorSlab && isEdge) {
            block = trimBlock; // floor/ceiling trim band
          } else if (isEdge && isWindowRow && !isCorner && style !== 'house') {
            block = BlockId.GLASS; // windows on middle row, not corners
          } else if (isEdge) {
            block = wallBlock;
          }
          // Ground floor entrance (door gap on south face)
          if (floorIdx === 0 && dz === 0 && (dx === 3 || dx === 4) && (dy === 1 || dy === 2)) {
            block = 0;
          }
          // Rooftop trim
          if (isTopFloor && isEdge) {
            block = trimBlock;
          }

          if (block !== 0) {
            this._setInChunk(chunk, lx, ly, lz, block);
          }
        }
      }
    }
  }

  _placeLampPost(chunk, lx, ly, lz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    // 4-block iron-fence-style pole topped with glowstone
    for (let dy = 1; dy <= 4; dy++) {
      const px = lx - wx0;
      const py = ly + dy - wy0;
      const pz = lz - wz0;
      if (px < 0 || px >= CHUNK_SIZE || py < 0 || py >= CHUNK_SIZE || pz < 0 || pz >= CHUNK_SIZE) continue;
      const block = dy === 4 ? BlockId.GLOWSTONE : BlockId.COBBLESTONE;
      this._setInChunk(chunk, px, py, pz, block);
    }
  }

  // --- Villa Miseria: A poor village with broken structures, few resources ---
  _placeVillaMiseria(chunk, vcx, surfaceH, vcz) {
    // Expanded to ~15 structures: more chozas, ruinas, fogatas, garbage piles
    const structures = [
      { dx:   0, dz:   0, type: 'dry_well' },
      // First ring of chozas
      { dx:   5, dz:   5, type: 'shack' },
      { dx:  -5, dz:   4, type: 'shack' },
      { dx:   5, dz:  -5, type: 'shack' },
      { dx:   0, dz:  -7, type: 'shack' },
      // Second ring (further out)
      { dx:  10, dz:   0, type: 'shack' },
      { dx: -10, dz:  -2, type: 'shack' },
      { dx:   2, dz:  11, type: 'shack' },
      { dx:  -3, dz: -11, type: 'shack' },
      { dx:   9, dz:   9, type: 'shack' },
      { dx: -10, dz:   8, type: 'shack' },
      // Empty fields
      { dx:  -6, dz:  -4, type: 'empty_field' },
      { dx:   7, dz:  -9, type: 'empty_field' },
      // Ruins (more broken structures)
      { dx:  -4, dz:   8, type: 'ruins' },
      { dx:  11, dz:   4, type: 'ruins' },
      { dx:  -8, dz: -8,  type: 'ruins' },
      // Fogatas (campfires) — survival warmth in the slum
      { dx:   3, dz:   2, type: 'campfire' },
      { dx:  -6, dz:   1, type: 'campfire' },
      { dx:   1, dz:  -3, type: 'campfire' },
      // Pilas de basura (garbage piles)
      { dx:   6, dz:   2, type: 'garbage' },
      { dx:  -2, dz:   6, type: 'garbage' },
      { dx:  -7, dz:  -7, type: 'garbage' },
      { dx:   8, dz:  -3, type: 'garbage' },
    ];

    for (const s of structures) {
      const sx = vcx + s.dx;
      const sz = vcz + s.dz;
      const sh = this._getHeight(sx, sz);
      if (sh <= SEA_LEVEL + 2) continue;

      if (s.type === 'shack') {
        this._placeShack(chunk, sx, sh, sz);
      } else if (s.type === 'empty_field') {
        this._placeEmptyField(chunk, sx, sh, sz);
      } else if (s.type === 'dry_well') {
        this._placeDryWell(chunk, sx, sh, sz);
      } else if (s.type === 'ruins') {
        this._placeRuins(chunk, sx, sh, sz);
      } else if (s.type === 'campfire') {
        this._placeCampfireSpot(chunk, sx, sh, sz);
      } else if (s.type === 'garbage') {
        this._placeGarbagePile(chunk, sx, sh, sz);
      }
    }

    // Dirt paths (no cobblestone - too poor)
    for (const s of structures) {
      const sx = vcx + s.dx;
      const sz = vcz + s.dz;
      this._placeDirtPath(chunk, vcx, vcz, sx, sz);
    }
  }

  // Small lit campfire ring (cobblestone ring + campfire block in center)
  _placeCampfireSpot(chunk, fx, fy, fz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Ring of cobblestone on the ground
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (Math.abs(dx) + Math.abs(dz) === 2) continue; // only sides
        if (dx === 0 && dz === 0) continue;              // skip center
        this._setInChunk(chunk, fx + dx - wx0, fy - wy0, fz + dz - wz0, BlockId.COBBLESTONE);
      }
    }
    // Campfire block in center if available, otherwise glowing torch
    if (BlockId.CAMPFIRE_LIT !== undefined) {
      this._setInChunk(chunk, fx - wx0, fy + 1 - wy0, fz - wz0, BlockId.CAMPFIRE_LIT);
    } else if (BlockId.TORCH !== undefined) {
      this._setInChunk(chunk, fx - wx0, fy + 1 - wy0, fz - wz0, BlockId.TORCH);
    }
  }

  // Garbage pile: stacked dirt + scattered planks (low scattered debris)
  _placeGarbagePile(chunk, gx, gy, gz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    const rng = (a, b) => Math.abs(this.treeDetail(a, b));

    // Mound of dirt with random planks/cobble mixed in
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const wx = gx + dx;
        const wz = gz + dz;
        const r = rng(wx * 3.1, wz * 3.1);
        if (r < 0.2) continue;
        const block = r > 0.7 ? BlockId.PLANKS : (r > 0.45 ? BlockId.COBBLESTONE : BlockId.DIRT);
        this._setInChunk(chunk, wx - wx0, gy + 1 - wy0, wz - wz0, block);
        // Some piles are 2 high
        if (r > 0.6 && dx === 0 && dz === 0) {
          this._setInChunk(chunk, wx - wx0, gy + 2 - wy0, wz - wz0, BlockId.DIRT);
        }
      }
    }
  }

  // Shack: small broken house with dirt floor, missing blocks, no glass
  _placeShack(chunk, hx, hy, hz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    const rng = Math.abs(this.treeDetail(hx * 0.7, hz * 0.7));

    // 4x3x4 shack (smaller than normal house)
    for (let dy = 1; dy <= 3; dy++) {
      for (let dx = 0; dx < 4; dx++) {
        for (let dz = 0; dz < 4; dz++) {
          const wx = hx + dx;
          const wy = hy + dy;
          const wz = hz + dz;

          let block = BlockId.AIR;

          // Floor: dirt (too poor for planks)
          if (dy === 1) {
            block = BlockId.DIRT;
          }
          // Walls: partial cobblestone and dirt, some blocks missing
          else if (dy <= 2 && (dx === 0 || dx === 3 || dz === 0 || dz === 3)) {
            const wallRng = Math.abs(this.treeDetail((wx + dy) * 3.7, wz * 3.7));
            if (wallRng > 0.3) {
              block = wallRng > 0.6 ? BlockId.COBBLESTONE : BlockId.DIRT;
            }
            // Door opening
            if (dz === 0 && dx === 1 && dy <= 2) {
              block = BlockId.AIR;
            }
          }
          // Roof: partial planks (some missing)
          else if (dy === 3) {
            const roofRng = Math.abs(this.treeDetail(wx * 2.3, wz * 2.3 + 100));
            if (roofRng > 0.25) {
              block = BlockId.PLANKS;
            }
          }

          if (block !== BlockId.AIR) {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, block);
          }
        }
      }
    }
  }

  // Empty field: dirt with no crops (failed harvest)
  _placeEmptyField(chunk, fx, fy, fz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let dx = 0; dx < 4; dx++) {
      for (let dz = 0; dz < 4; dz++) {
        const wx = fx + dx;
        const wz = fz + dz;
        // Just bare dirt, no fence, no crops
        this._setInChunkForce(chunk, wx - wx0, fy - wy0, wz - wz0, BlockId.DIRT);
      }
    }
  }

  // Dry well: cobblestone structure with no water
  _placeDryWell(chunk, wx, wy, wz) {
    const cx0 = chunk.cx * CHUNK_SIZE;
    const cy0 = chunk.cy * CHUNK_SIZE;
    const cz0 = chunk.cz * CHUNK_SIZE;

    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        this._setInChunk(chunk, wx + dx - cx0, wy - cy0, wz + dz - cz0, BlockId.COBBLESTONE);
        if (Math.abs(dx) + Math.abs(dz) <= 1 && !(dx === 0 && dz === 0)) {
          this._setInChunk(chunk, wx + dx - cx0, wy + 1 - cy0, wz + dz - cz0, BlockId.COBBLESTONE);
        }
        // No water! The well is dry
      }
    }
  }

  // Ruins: collapsed structure
  _placeRuins(chunk, rx, ry, rz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Scattered cobblestone blocks on the ground
    for (let dx = -1; dx <= 2; dx++) {
      for (let dz = -1; dz <= 2; dz++) {
        const rng = Math.abs(this.treeDetail((rx + dx) * 4.1, (rz + dz) * 4.1));
        if (rng > 0.4) {
          const wx = rx + dx;
          const wz = rz + dz;
          this._setInChunk(chunk, wx - wx0, ry + 1 - wy0, wz - wz0, BlockId.COBBLESTONE);
          // Some blocks stacked up to 2 high
          if (rng > 0.75) {
            this._setInChunk(chunk, wx - wx0, ry + 2 - wy0, wz - wz0, BlockId.COBBLESTONE);
          }
        }
      }
    }
  }

  // Dirt path: just dirt on the surface (Villa Miseria has no cobblestone paths)
  _placeDirtPath(chunk, x1, z1, x2, z2) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const dx = x2 - x1;
    const dz = z2 - z1;
    const steps = Math.max(Math.abs(dx), Math.abs(dz));
    if (steps === 0) return;

    for (let i = 0; i <= steps; i++) {
      const wx = Math.round(x1 + dx * i / steps);
      const wz = Math.round(z1 + dz * i / steps);
      const wy = this._getHeight(wx, wz);
      if (wy <= SEA_LEVEL) continue;

      this._setInChunkForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.DIRT);
    }
  }

  // --- Villa Prosperidad: Rich village with large houses, glass, gold ---
  _placeVillaProsperidad(chunk, vcx, surfaceH, vcz) {
    const structures = [
      { dx: 0, dz: 0, type: 'fountain' },
      { dx: 8, dz: 8, type: 'mansion' },
      { dx: -8, dz: 7, type: 'mansion' },
      { dx: 8, dz: -7, type: 'mansion' },
      { dx: -9, dz: -6, type: 'garden' },
      { dx: 0, dz: -10, type: 'mansion' },
      { dx: 0, dz: 10, type: 'tower' },
      { dx: -10, dz: -12, type: 'university' },
    ];

    for (const s of structures) {
      const sx = vcx + s.dx;
      const sz = vcz + s.dz;
      const sh = this._getHeight(sx, sz);
      if (sh <= SEA_LEVEL + 2) continue;

      if (s.type === 'mansion') {
        this._placeMansion(chunk, sx, sh, sz);
      } else if (s.type === 'garden') {
        this._placeGarden(chunk, sx, sh, sz);
      } else if (s.type === 'fountain') {
        this._placeFountain(chunk, sx, sh, sz);
      } else if (s.type === 'tower') {
        this._placeTower(chunk, sx, sh, sz);
      } else if (s.type === 'university') {
        this._placeUniversity(chunk, sx, sh, sz);
      }
    }

    // Stone paths
    for (const s of structures) {
      this._placePath(chunk, vcx, vcz, vcx + s.dx, vcz + s.dz);
    }
  }

  // Mansion: large house with glass windows, planks, door
  _placeMansion(chunk, hx, hy, hz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // 7x5x7 mansion
    for (let dy = 1; dy <= 5; dy++) {
      for (let dx = 0; dx < 7; dx++) {
        for (let dz = 0; dz < 7; dz++) {
          const wx = hx + dx;
          const wy = hy + dy;
          const wz = hz + dz;
          let block = BlockId.AIR;

          // Floor (planks)
          if (dy === 1) {
            block = BlockId.PLANKS;
          }
          // Walls
          else if (dy <= 4 && (dx === 0 || dx === 6 || dz === 0 || dz === 6)) {
            block = BlockId.COBBLESTONE;
            // Large windows
            if (dy >= 2 && dy <= 3) {
              if ((dx === 3 && (dz === 0 || dz === 6)) || (dz === 3 && (dx === 0 || dx === 6))) {
                block = BlockId.GLASS;
              }
              if ((dx === 2 || dx === 4) && (dz === 0 || dz === 6)) {
                block = BlockId.GLASS;
              }
              if ((dz === 2 || dz === 4) && (dx === 0 || dx === 6)) {
                block = BlockId.GLASS;
              }
            }
            // Door
            if (dz === 0 && dx === 3 && dy <= 2) {
              block = BlockId.DOOR_CLOSED;
            }
          }
          // Roof
          else if (dy === 5) {
            block = BlockId.PLANKS;
          }

          if (block !== BlockId.AIR) {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, block);
          }
        }
      }
    }
    // Decorative blocks on corners (like pillars)
    for (let dy = 1; dy <= 4; dy++) {
      this._setInChunk(chunk, hx - wx0, hy + dy - wy0, hz - wz0, BlockId.STONE);
      this._setInChunk(chunk, hx + 6 - wx0, hy + dy - wy0, hz - wz0, BlockId.STONE);
      this._setInChunk(chunk, hx - wx0, hy + dy - wy0, hz + 6 - wz0, BlockId.STONE);
      this._setInChunk(chunk, hx + 6 - wx0, hy + dy - wy0, hz + 6 - wz0, BlockId.STONE);
    }
  }

  // Garden: fenced area with wheat, flowers (colored blocks)
  _placeGarden(chunk, fx, fy, fz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let dx = 0; dx < 6; dx++) {
      for (let dz = 0; dz < 6; dz++) {
        const wx = fx + dx;
        const wz = fz + dz;
        if (dx === 0 || dx === 5 || dz === 0 || dz === 5) {
          this._setInChunk(chunk, wx - wx0, fy + 1 - wy0, wz - wz0, BlockId.WOOD);
        } else {
          this._setInChunkForce(chunk, wx - wx0, fy - wy0, wz - wz0, BlockId.DIRT);
          this._setInChunk(chunk, wx - wx0, fy + 1 - wy0, wz - wz0, BlockId.WHEAT);
        }
      }
    }
  }

  // Fountain: cobblestone basin with water, raised
  _placeFountain(chunk, wx, wy, wz) {
    const cx0 = chunk.cx * CHUNK_SIZE;
    const cy0 = chunk.cy * CHUNK_SIZE;
    const cz0 = chunk.cz * CHUNK_SIZE;

    // Basin
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        this._setInChunk(chunk, wx + dx - cx0, wy - cy0, wz + dz - cz0, BlockId.STONE);
        if (Math.abs(dx) === 2 || Math.abs(dz) === 2) {
          this._setInChunk(chunk, wx + dx - cx0, wy + 1 - cy0, wz + dz - cz0, BlockId.STONE);
        } else {
          this._setInChunk(chunk, wx + dx - cx0, wy + 1 - cy0, wz + dz - cz0, BlockId.WATER);
        }
      }
    }
    // Center column
    for (let dy = 1; dy <= 4; dy++) {
      this._setInChunk(chunk, wx - cx0, wy + dy - cy0, wz - cz0, BlockId.STONE);
    }
    // Water top
    this._setInChunk(chunk, wx - cx0, wy + 4 - cy0, wz - cz0, BlockId.WATER);
  }

  // Tower: tall stone structure with glass at top
  _placeTower(chunk, tx, ty, tz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // 3x3 base, 8 blocks tall
    for (let dy = 1; dy <= 8; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          const wx = tx + dx;
          const wy = ty + dy;
          const wz = tz + dz;
          // Walls only
          if (dx === 0 && dz === 0 && dy < 8) continue;
          // Glass on upper floors
          if (dy >= 6 && dy <= 7 && (dx === 0 || dz === 0) && !(dx === 0 && dz === 0)) {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.GLASS);
          } else {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.STONE);
          }
        }
      }
    }
    // Roof overhang
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        this._setInChunk(chunk, tx + dx - wx0, ty + 9 - wy0, tz + dz - wz0, BlockId.PLANKS);
      }
    }
  }

  // University: large stone building with multiple floors, glass windows, sign area
  _placeUniversity(chunk, ux, uy, uz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // 9x7x9 large building (width x height x depth)
    for (let dy = 1; dy <= 7; dy++) {
      for (let dx = 0; dx < 9; dx++) {
        for (let dz = 0; dz < 9; dz++) {
          const wx = ux + dx;
          const wy = uy + dy;
          const wz = uz + dz;
          let block = BlockId.AIR;

          // Floor 1 and floor 2 (planks)
          if (dy === 1 || dy === 4) {
            block = BlockId.PLANKS;
          }
          // Walls
          else if (dx === 0 || dx === 8 || dz === 0 || dz === 8) {
            block = BlockId.STONE;
            // Large glass windows on all floors
            if (dy === 2 || dy === 3 || dy === 5 || dy === 6) {
              if (dx > 1 && dx < 7 && (dz === 0 || dz === 8)) block = BlockId.GLASS;
              if (dz > 1 && dz < 7 && (dx === 0 || dx === 8)) block = BlockId.GLASS;
            }
            // Main entrance (double wide)
            if (dz === 0 && (dx === 3 || dx === 4 || dx === 5) && dy <= 3 && dy >= 2) {
              block = BlockId.AIR;
            }
            // Doors at entrance
            if (dz === 0 && dx === 4 && dy <= 2) {
              block = BlockId.DOOR_CLOSED;
            }
          }
          // Roof
          else if (dy === 7) {
            block = BlockId.STONE;
          }

          if (block !== BlockId.AIR) {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, block);
          }
        }
      }
    }

    // Stone pillars at entrance
    for (let dy = 1; dy <= 6; dy++) {
      this._setInChunk(chunk, ux + 2 - wx0, uy + dy - wy0, uz - 1 - wz0, BlockId.STONE);
      this._setInChunk(chunk, ux + 6 - wx0, uy + dy - wy0, uz - 1 - wz0, BlockId.STONE);
    }
    // Entrance roof overhang
    for (let dx = 2; dx <= 6; dx++) {
      this._setInChunk(chunk, ux + dx - wx0, uy + 4 - wy0, uz - 1 - wz0, BlockId.STONE);
    }

    // Roof decorative border
    for (let dx = -1; dx <= 9; dx++) {
      this._setInChunk(chunk, ux + dx - wx0, uy + 7 - wy0, uz - 1 - wz0, BlockId.STONE);
      this._setInChunk(chunk, ux + dx - wx0, uy + 7 - wy0, uz + 9 - wz0, BlockId.STONE);
    }
    for (let dz = -1; dz <= 9; dz++) {
      this._setInChunk(chunk, ux - 1 - wx0, uy + 7 - wy0, uz + dz - wz0, BlockId.STONE);
      this._setInChunk(chunk, ux + 9 - wx0, uy + 7 - wy0, uz + dz - wz0, BlockId.STONE);
    }

    // Interior: wooden desks/tables (planks blocks inside)
    // Ground floor - lecture hall
    for (let row = 0; row < 3; row++) {
      for (let col = 1; col <= 6; col++) {
        this._setInChunk(chunk, ux + col - wx0, uy + 2 - wy0, uz + 3 + row * 2 - wz0, BlockId.PLANKS);
      }
    }
    // Second floor - library (more planks as bookshelves along walls)
    for (let dx = 1; dx <= 7; dx++) {
      this._setInChunk(chunk, ux + dx - wx0, uy + 5 - wy0, uz + 1 - wz0, BlockId.WOOD);
      this._setInChunk(chunk, ux + dx - wx0, uy + 6 - wy0, uz + 1 - wz0, BlockId.WOOD);
      this._setInChunk(chunk, ux + dx - wx0, uy + 5 - wy0, uz + 7 - wz0, BlockId.WOOD);
      this._setInChunk(chunk, ux + dx - wx0, uy + 6 - wy0, uz + 7 - wz0, BlockId.WOOD);
    }
    // Study desks on second floor
    for (let col = 2; col <= 6; col += 2) {
      this._setInChunk(chunk, ux + col - wx0, uy + 5 - wy0, uz + 4 - wz0, BlockId.PLANKS);
    }
    // Stairs connecting floors (cobblestone steps)
    for (let i = 0; i < 3; i++) {
      this._setInChunkForce(chunk, ux + 7 - wx0, uy + 2 + i - wy0, uz + 2 + i - wz0, BlockId.COBBLESTONE);
    }
  }

  _placeVillage(chunk, vcx, surfaceH, vcz) {
    // Place several structures around the village center
    const structures = [
      { dx: 0, dz: 0, type: 'well' },
      { dx: 6, dz: 6, type: 'house' },
      { dx: -6, dz: 6, type: 'house' },
      { dx: 6, dz: -6, type: 'house' },
      { dx: -7, dz: -5, type: 'farm' },
      { dx: 0, dz: -8, type: 'house' },
    ];

    for (const s of structures) {
      const sx = vcx + s.dx;
      const sz = vcz + s.dz;
      const sh = this._getHeight(sx, sz);
      if (sh <= SEA_LEVEL + 2) continue;

      if (s.type === 'house') {
        this._placeHouse(chunk, sx, sh, sz);
      } else if (s.type === 'farm') {
        this._placeFarm(chunk, sx, sh, sz);
      } else if (s.type === 'well') {
        this._placeWell(chunk, sx, sh, sz);
      }
    }

    // Paths (cobblestone on ground connecting structures)
    for (const s of structures) {
      const sx = vcx + s.dx;
      const sz = vcz + s.dz;
      this._placePath(chunk, vcx, vcz, sx, sz);
    }
  }

  _placeHouse(chunk, hx, hy, hz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // 5x4x5 house (width x height x depth)
    for (let dy = 1; dy <= 4; dy++) {
      for (let dx = 0; dx < 5; dx++) {
        for (let dz = 0; dz < 5; dz++) {
          const wx = hx + dx;
          const wy = hy + dy;
          const wz = hz + dz;

          let block = BlockId.AIR;

          // Floor
          if (dy === 1) {
            block = BlockId.PLANKS;
          }
          // Walls
          else if (dy <= 3 && (dx === 0 || dx === 4 || dz === 0 || dz === 4)) {
            block = BlockId.COBBLESTONE;
            // Windows
            if (dy === 2 && ((dx === 2 && (dz === 0 || dz === 4)) || (dz === 2 && (dx === 0 || dx === 4)))) {
              block = BlockId.GLASS;
            }
            // Door
            if (dz === 0 && dx === 2 && dy <= 2) {
              block = BlockId.DOOR_CLOSED;
            }
          }
          // Roof
          else if (dy === 4) {
            block = BlockId.PLANKS;
          }

          if (block !== BlockId.AIR) {
            this._setInChunk(chunk, wx - wx0, wy - wy0, wz - wz0, block);
          }
        }
      }
    }
  }

  _placeFarm(chunk, fx, fy, fz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // 5x5 farm with wheat
    for (let dx = 0; dx < 5; dx++) {
      for (let dz = 0; dz < 5; dz++) {
        const wx = fx + dx;
        const wz = fz + dz;
        // Border is wood fence (use wood)
        if (dx === 0 || dx === 4 || dz === 0 || dz === 4) {
          this._setInChunk(chunk, wx - wx0, fy + 1 - wy0, wz - wz0, BlockId.WOOD);
        } else {
          // Dirt ground
          this._setInChunk(chunk, wx - wx0, fy - wy0, wz - wz0, BlockId.DIRT);
          // Wheat on top
          this._setInChunk(chunk, wx - wx0, fy + 1 - wy0, wz - wz0, BlockId.WHEAT);
        }
      }
    }
  }

  _placeWell(chunk, wx, wy, wz) {
    const cx0 = chunk.cx * CHUNK_SIZE;
    const cy0 = chunk.cy * CHUNK_SIZE;
    const cz0 = chunk.cz * CHUNK_SIZE;

    // 3x3 well
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        // Cobblestone base
        this._setInChunk(chunk, wx + dx - cx0, wy - cy0, wz + dz - cz0, BlockId.COBBLESTONE);

        if (dx === 0 && dz === 0) {
          // Water in center
          this._setInChunk(chunk, wx - cx0, wy + 1 - cy0, wz - cz0, BlockId.WATER);
        } else if (Math.abs(dx) + Math.abs(dz) <= 1) {
          // Short walls
          this._setInChunk(chunk, wx + dx - cx0, wy + 1 - cy0, wz + dz - cz0, BlockId.COBBLESTONE);
        }
      }
    }
    // Corner posts
    for (const [dx, dz] of [[-1,-1],[1,-1],[-1,1],[1,1]]) {
      this._setInChunk(chunk, wx + dx - cx0, wy + 1 - cy0, wz + dz - cz0, BlockId.COBBLESTONE);
      this._setInChunk(chunk, wx + dx - cx0, wy + 2 - cy0, wz + dz - cz0, BlockId.WOOD);
    }
    // Roof
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        this._setInChunk(chunk, wx + dx - cx0, wy + 3 - cy0, wz + dz - cz0, BlockId.PLANKS);
      }
    }
  }

  _placePath(chunk, x1, z1, x2, z2) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Simple line from (x1,z1) to (x2,z2)
    const dx = x2 - x1;
    const dz = z2 - z1;
    const steps = Math.max(Math.abs(dx), Math.abs(dz));
    if (steps === 0) return;

    for (let i = 0; i <= steps; i++) {
      const wx = Math.round(x1 + dx * i / steps);
      const wz = Math.round(z1 + dz * i / steps);
      const wy = this._getHeight(wx, wz);
      if (wy <= SEA_LEVEL) continue;

      this._setInChunkForce(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.COBBLESTONE);
    }
  }

  // --- Underground Structures ---
  // (Dungeons, Mines, Desert Temples, and Ruined Portals are now
  //  handled by StructureGenerator — see _generateStructures above)

  _setInChunk(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      const current = chunk.getBlock(lx, ly, lz);
      if (current === BlockId.AIR || current === BlockId.LEAVES || current === BlockId.JUNGLE_LEAVES || current === BlockId.ACACIA_LEAVES) {
        chunk.setBlock(lx, ly, lz, blockId);
      }
    }
  }

  // Force-set (overwrite any block, for structures)
  _setInChunkForce(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      chunk.setBlock(lx, ly, lz, blockId);
    }
  }
}
